interface Project {
  id: number;
  thumbnailUrl: string;
  icon1Url: string;
  icon2Url: string;
  title: string;
  badge: string;
  type: string;
  stats: {
    month: number;
    tags: number;
    nextMilestone: string;
  };
  isFavorite: boolean;
}

interface ProjectTableProps {
  projects: Project[];
  onFavoriteToggle: (projectId: number) => void;
  onProjectClick?: (projectId: number) => void;
}

export function ProjectTable({
  projects,
  onFavoriteToggle,
  onProjectClick,
}: ProjectTableProps) {
  return (
    <div className="content-stretch flex flex-col gap-[2px] items-center justify-center relative w-full overflow-x-auto">
      <div className="content-stretch flex flex-col items-start relative shrink-0 min-w-full">
        {/* Table Header */}
        <div className="content-stretch bg-[#f7f8fa] flex gap-0 items-start relative shrink-0 w-full min-w-[1200px]">
          {/* Star Column Header */}
          <div className="border-b-[2px] border-solid border-[rgba(11,18,40,0.14)] box-border content-stretch flex h-[45px] items-center justify-center relative shrink-0 w-[40px]">
            <div className="content-stretch flex items-start relative shrink-0">
              <div className="overflow-clip relative shrink-0 size-[16px]" />
            </div>
          </div>

          {/* Checkbox Column Header */}
          <div className="border-b-[2px] border-solid border-[rgba(11,18,40,0.14)] box-border content-stretch flex h-[45px] items-center justify-center relative shrink-0 w-[40px]">
            <div className="relative shrink-0 size-[24px]">
              <div className="absolute border border-solid border-[rgba(11,18,40,0.14)] inset-1/4 rounded-[2px]" />
            </div>
          </div>

          {/* Name Column Header */}
          <div className="basis-0 border-b-[2px] border-solid border-[rgba(11,18,40,0.14)] box-border content-stretch flex flex-col grow h-[45px] items-start justify-center min-h-px min-w-px relative shrink-0 pl-[8px]">
            <div className="content-stretch flex items-center overflow-clip relative shrink-0 w-full">
              <p
                className="font-['Noto_Sans:Bold',sans-serif] font-bold leading-[16px] relative shrink-0 text-[#292a2e] text-[12px] text-nowrap whitespace-pre"
                style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
              >
                Name
              </p>
            </div>
          </div>

          {/* Project Logo Column Header */}
          <div className="basis-0 border-b-[2px] border-solid border-[rgba(11,18,40,0.14)] box-border content-stretch flex flex-col grow h-[45px] items-start justify-center min-h-px min-w-px relative shrink-0 pl-[8px]">
            <div className="content-stretch flex items-center overflow-clip relative shrink-0 w-full">
              <p
                className="font-['Noto_Sans:Bold',sans-serif] font-bold leading-[16px] relative shrink-0 text-[#292a2e] text-[12px] text-nowrap whitespace-pre"
                style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
              >
                Project logo
              </p>
            </div>
          </div>

          {/* Client Logo Column Header */}
          <div className="basis-0 border-b-[2px] border-solid border-[rgba(11,18,40,0.14)] box-border content-stretch flex flex-col grow h-[45px] items-start justify-center min-h-px min-w-px relative shrink-0 pl-[8px]">
            <div className="content-stretch flex items-center overflow-clip relative shrink-0 w-full">
              <p
                className="font-['Noto_Sans:Bold',sans-serif] font-bold leading-[16px] relative shrink-0 text-[#292a2e] text-[12px] text-nowrap whitespace-pre"
                style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
              >
                Client logo
              </p>
            </div>
          </div>

          {/* Project Type Column Header */}
          <div className="basis-0 border-b-[2px] border-solid border-[rgba(11,18,40,0.14)] box-border content-stretch flex flex-col grow h-[45px] items-start justify-center min-h-px min-w-px relative shrink-0 pl-[8px]">
            <div className="content-stretch flex items-center overflow-clip relative shrink-0 w-full">
              <p
                className="font-['Noto_Sans:Bold',sans-serif] font-bold leading-[16px] relative shrink-0 text-[#292a2e] text-[12px] text-nowrap whitespace-pre"
                style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
              >
                Project type
              </p>
            </div>
          </div>

          {/* Project Code Column Header */}
          <div className="basis-0 border-b-[2px] border-solid border-[rgba(11,18,40,0.14)] box-border content-stretch flex flex-col grow h-[45px] items-start justify-center min-h-px min-w-px relative shrink-0 pl-[8px]">
            <div className="content-stretch flex items-center overflow-clip relative shrink-0 w-full">
              <p
                className="font-['Noto_Sans:Bold',sans-serif] font-bold leading-[16px] relative shrink-0 text-[#292a2e] text-[12px] text-nowrap whitespace-pre"
                style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
              >
                Project code
              </p>
            </div>
          </div>

          {/* Month Column Header */}
          <div className="basis-0 border-b-[2px] border-solid border-[rgba(11,18,40,0.14)] box-border content-stretch flex flex-col grow h-[45px] items-start justify-center min-h-px min-w-px relative shrink-0 pl-[8px]">
            <div className="content-stretch flex items-center overflow-clip relative shrink-0 w-full">
              <p
                className="font-['Noto_Sans:Bold',sans-serif] font-bold leading-[16px] relative shrink-0 text-[#292a2e] text-[12px] text-nowrap whitespace-pre"
                style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
              >
                Month
              </p>
            </div>
          </div>

          {/* Tags Column Header */}
          <div className="basis-0 border-b-[2px] border-solid border-[rgba(11,18,40,0.14)] box-border content-stretch flex flex-col grow h-[45px] items-start justify-center min-h-px min-w-px relative shrink-0 pl-[8px]">
            <div className="content-stretch flex items-center overflow-clip relative shrink-0 w-full">
              <p
                className="font-['Noto_Sans:Bold',sans-serif] font-bold leading-[16px] relative shrink-0 text-[#292a2e] text-[12px] text-nowrap whitespace-pre"
                style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
              >
                Tags
              </p>
            </div>
          </div>

          {/* Next Milestone Column Header */}
          <div className="basis-0 border-b-[2px] border-solid border-[rgba(11,18,40,0.14)] box-border content-stretch flex flex-col grow h-[45px] items-start justify-center min-h-px min-w-px relative shrink-0 pl-[8px]">
            <div className="content-stretch flex items-center overflow-clip relative shrink-0 w-full">
              <p
                className="font-['Noto_Sans:Bold',sans-serif] font-bold leading-[16px] relative shrink-0 text-[#292a2e] text-[12px] text-nowrap whitespace-pre"
                style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
              >
                Next milestone
              </p>
            </div>
          </div>
        </div>

        {/* Table Rows */}
        {projects.map((project) => (
          <div
            key={project.id}
            onClick={() => onProjectClick?.(project.id)}
            className="content-stretch flex gap-0 h-[54px] items-start relative shrink-0 w-full min-w-[1200px] cursor-pointer hover:bg-[rgba(5,21,36,0.02)] transition-colors"
          >
            {/* Star Column */}
            <div className="border-b border-solid border-[rgba(11,18,40,0.14)] box-border content-stretch flex h-full items-center justify-center min-h-[40px] pl-[8px] py-0 relative shrink-0 w-[40px]">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onFavoriteToggle(project.id);
                }}
                className="overflow-clip relative shrink-0 size-[24px] cursor-pointer bg-transparent border-none p-0"
              >
                <div className="absolute inset-[10.06%_8.33%_10.05%_8.33%]">
                  <svg
                    className="block max-w-none size-full"
                    viewBox="0 0 20 19"
                    fill="none"
                  >
                    <path
                      d="M10 0L13.09 6.26L20 7.27L15 12.14L16.18 19L10 15.77L3.82 19L5 12.14L0 7.27L6.91 6.26L10 0Z"
                      stroke="#292A2E"
                      strokeWidth="1.5"
                    />
                  </svg>
                </div>
              </button>
            </div>

            {/* Checkbox Column */}
            <div className="border-b border-solid border-[rgba(11,18,40,0.14)] box-border content-stretch flex h-full items-center justify-center min-h-[40px] py-0 relative shrink-0 w-[40px]">
              <div className="relative shrink-0 size-[24px]">
                <div className="absolute border border-solid border-[rgba(11,18,40,0.14)] inset-1/4 rounded-[2px]" />
              </div>
            </div>

            {/* Name Column */}
            <div className="basis-0 border-b border-solid border-[rgba(11,18,40,0.14)] box-border content-stretch flex grow h-full items-center min-h-[40px] min-w-px pl-[8px] py-0 relative shrink-0">
              <div className="basis-0 content-stretch flex flex-col grow items-start justify-center min-h-px min-w-px relative shrink-0">
                <p
                  className="font-['Noto_Sans:Regular',sans-serif] font-normal leading-[20px] overflow-ellipsis overflow-hidden relative shrink-0 text-[#292a2e] text-[14px] text-nowrap w-full"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  {project.title}
                </p>
              </div>
            </div>

            {/* Project Logo Column */}
            <div className="basis-0 border-b border-solid border-[rgba(11,18,40,0.14)] box-border content-stretch flex grow h-full items-center min-h-[40px] min-w-px pl-[8px] py-0 relative shrink-0">
              <div className="content-stretch flex gap-[10px] items-center relative shrink-0">
                <div className="overflow-clip relative shrink-0 size-[24px] rounded-[3px]">
                  <img
                    alt=""
                    className="block max-w-none size-full object-cover"
                    src={project.icon1Url}
                  />
                </div>
              </div>
            </div>

            {/* Client Logo Column */}
            <div className="basis-0 border-b border-solid border-[rgba(11,18,40,0.14)] box-border content-stretch flex grow h-full items-center min-h-[40px] min-w-px pl-[8px] py-0 relative shrink-0">
              <div className="content-stretch flex gap-[10px] items-center relative shrink-0">
                <div className="overflow-clip relative shrink-0 size-[24px] rounded-[3px]">
                  <img
                    alt=""
                    className="block max-w-none size-full object-cover"
                    src={project.icon2Url}
                  />
                </div>
              </div>
            </div>

            {/* Project Type Column */}
            <div className="basis-0 border-b border-solid border-[rgba(11,18,40,0.14)] box-border content-stretch flex grow h-full items-center min-h-[40px] min-w-px pl-[8px] py-0 relative shrink-0">
              <div className="basis-0 content-stretch flex flex-col grow items-start justify-center min-h-px min-w-px relative shrink-0">
                <p
                  className="font-['Noto_Sans:Regular',sans-serif] font-normal leading-[20px] overflow-ellipsis overflow-hidden relative shrink-0 text-[#292a2e] text-[14px] text-nowrap w-full"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  {project.type}
                </p>
              </div>
            </div>

            {/* Project Code Column */}
            <div className="basis-0 border-b border-solid border-[rgba(11,18,40,0.14)] box-border content-stretch flex grow h-full items-center min-h-[40px] min-w-px pl-[8px] py-0 relative shrink-0">
              <div className="basis-0 content-stretch flex flex-col grow items-start justify-center min-h-px min-w-px relative shrink-0">
                <p
                  className="font-['Noto_Sans:Regular',sans-serif] font-normal leading-[20px] overflow-ellipsis overflow-hidden relative shrink-0 text-[#292a2e] text-[14px] text-nowrap w-full"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  {project.badge}
                </p>
              </div>
            </div>

            {/* Month Column */}
            <div className="basis-0 border-b border-solid border-[rgba(11,18,40,0.14)] box-border content-stretch flex grow h-full items-center min-h-[40px] min-w-px pl-[8px] py-0 relative shrink-0">
              <div className="basis-0 content-stretch flex flex-col grow items-start justify-center min-h-px min-w-px relative shrink-0">
                <p
                  className="font-['Noto_Sans:Regular',sans-serif] font-normal leading-[20px] overflow-ellipsis overflow-hidden relative shrink-0 text-[#292a2e] text-[14px] text-nowrap w-full"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  {project.stats.month}
                </p>
              </div>
            </div>

            {/* Tags Column */}
            <div className="basis-0 border-b border-solid border-[rgba(11,18,40,0.14)] box-border content-stretch flex grow h-full items-center min-h-[40px] min-w-px pl-[8px] py-0 relative shrink-0">
              <div className="basis-0 content-stretch flex flex-col grow items-start justify-center min-h-px min-w-px relative shrink-0">
                <p
                  className="font-['Noto_Sans:Regular',sans-serif] font-normal leading-[20px] overflow-ellipsis overflow-hidden relative shrink-0 text-[#292a2e] text-[14px] text-nowrap w-full"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  {project.stats.tags.toLocaleString()}
                </p>
              </div>
            </div>

            {/* Next Milestone Column */}
            <div className="basis-0 border-b border-solid border-[rgba(11,18,40,0.14)] box-border content-stretch flex grow h-full items-center min-h-[40px] min-w-px pl-[8px] py-0 relative shrink-0">
              <div className="basis-0 content-stretch flex flex-col grow items-start justify-center min-h-px min-w-px relative shrink-0">
                <p
                  className="font-['Noto_Sans:Regular',sans-serif] font-normal leading-[20px] overflow-ellipsis overflow-hidden relative shrink-0 text-[#292a2e] text-[14px] text-nowrap w-full"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  {project.stats.nextMilestone}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
